import React, { useState } from 'react';
import { Button, List, Space, Modal, message, Typography, Card, Collapse, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ArrowRightOutlined, FolderOutlined } from '@ant-design/icons';
import { SharedStep, SharedStepCreate, SharedStepUpdate, Step } from '../../types/sharedStep';
import { SharedStepModal } from './SharedStepModal';
import { Folder } from '../../types/testItem';
import { SelectFolderModal } from './SelectFolderModal';
import { useTranslation } from 'react-i18next';

interface Props {
    steps: SharedStep[];
    projectId: number;
    folderId?: number;
    onAddToFolder: (step: SharedStep, folderId: number) => void;
    onUpdate: (step: SharedStep) => void;
    onDelete: (stepId: number) => void;
    onCreate: (data: SharedStepCreate) => void;
    folders: Folder[];
}

export const SharedStepsList: React.FC<Props> = ({
    steps,
    projectId,
    folderId,
    onAddToFolder,
    onUpdate,
    onDelete,
    onCreate,
    folders,
}) => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedStep, setSelectedStep] = useState<SharedStep | undefined>();
    const [isFolderModalVisible, setIsFolderModalVisible] = useState(false);
    const [stepToAddToFolder, setStepToAddToFolder] = useState<SharedStep | null>(null);
    const { t } = useTranslation();

    const handleEdit = (step: SharedStep) => {
        setSelectedStep(step);
        setIsModalVisible(true);
    };

    const handleCreate = () => {
        setSelectedStep(undefined);
        setIsModalVisible(true);
    };

    const handleDelete = async (stepId: number) => {
        Modal.confirm({
            title: 'Удалить общий шаг?',
            content: 'Это действие нельзя отменить.',
            okText: 'Удалить',
            okType: 'danger',
            cancelText: 'Отмена',
            onOk: async () => {
                await onDelete(stepId);
                message.success('Шаг удален');
            }
        });
    };

    const handleSubmit = async (data: SharedStepCreate | SharedStepUpdate) => {
        try {
            if (selectedStep) {
                await onUpdate({ ...selectedStep, ...data } as SharedStep);
            } else {
                await onCreate(data as SharedStepCreate);
            }
            setIsModalVisible(false);
            message.success(selectedStep ? t('sharedSteps.notifications.updateSuccess') : t('sharedSteps.notifications.createSuccess'));
        } catch (error) {
            console.error('Error saving shared step:', error);
            message.error(t('sharedSteps.notifications.saveError'));
        }
    };

    const handleAddToFolderClick = (step: SharedStep) => {
        setStepToAddToFolder(step);
        setIsFolderModalVisible(true);
    };

    const handleFolderSelect = (folder: Folder | number) => {
        const folderId = typeof folder === 'number' ? folder : folder.id;
        if (stepToAddToFolder) {
            onAddToFolder(stepToAddToFolder, folderId);
            message.success('Шаг добавлен к папке');
        }
    };

    return (
        <div style={{ padding: '16px' }}>
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography.Title level={4} style={{ margin: 0 }}>Общие шаги</Typography.Title>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreate}
                >
                    Создать общий шаг
                </Button>
            </div>

            <List
                dataSource={steps}
                renderItem={step => (
                    <List.Item
                        actions={[
                            <Button
                                type="text"
                                icon={<FolderOutlined />}
                                onClick={() => handleAddToFolderClick(step)}
                            >
                                Добавить к папке
                            </Button>,
                            <Button
                                type="text"
                                icon={<EditOutlined />}
                                onClick={() => handleEdit(step)}
                            >
                                Редактировать
                            </Button>,
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => handleDelete(step.id)}
                            >
                                Удалить
                            </Button>
                        ]}
                    >
                        <List.Item.Meta
                            title={step.name}
                            description={
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    {step.description && (
                                        <Typography.Text type="secondary">
                                            {step.description}
                                        </Typography.Text>
                                    )}
                                    <List
                                        size="small"
                                        dataSource={step.steps}
                                        renderItem={(s, index) => (
                                            <List.Item>
                                                <Space direction="vertical" style={{ width: '100%' }}>
                                                    <Typography.Text strong>
                                                        Шаг {index + 1}: {s.action}
                                                    </Typography.Text>
                                                    <Typography.Text>
                                                        Ожидаемый результат: {s.result?.expected}
                                                    </Typography.Text>
                                                </Space>
                                            </List.Item>
                                        )}
                                    />
                                </Space>
                            }
                        />
                    </List.Item>
                )}
            />

            <SharedStepModal
                open={isModalVisible}
                onClose={() => setIsModalVisible(false)}
                onSubmit={handleSubmit}
                projectId={projectId}
                folderId={folderId}
                sharedStep={selectedStep}
                folders={folders}
                currentFolderId={folderId}
            />

            <SelectFolderModal
                open={isFolderModalVisible}
                onClose={() => setIsFolderModalVisible(false)}
                folders={folders}
                onSelect={handleFolderSelect}
            />
        </div>
    );
}; 